import styles from "./LoyaltyPoints.module.scss";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import giftImage from "../../../assets/images/loyalty/gift.png";
import { useCallback, useMemo, useState } from "react";
import { ILoyaltyHistory, LoyaltyHistoryChargeTypes } from "../../../models/Loyalty";
import useAppSelector from "../../../hooks/useAppSelector";
import dayjs from "dayjs";
import { useGetLoyaltyHistoryQuery, useGetLoyaltyPointBalanceQuery } from "../../../api/queries";

const LoyaltyPoints = () => {
  const { t } = useTranslation();

  const { user } = useAppSelector((state) => state.userProfileSlice);

  const itemsPerPage = 4;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const skip = useMemo(() => (currentPage - 1) * itemsPerPage, [currentPage]);

  const { data: loyaltyHistoryData = { historyItemsLength: 0, historyItems: [] }, isLoading } =
    useGetLoyaltyHistoryQuery({
      userId: user?.id ?? "",
      limit: itemsPerPage,
      skip,
    });
  const { data: loyaltyBalance } = useGetLoyaltyPointBalanceQuery(user?.id || null);

  const totalPages = useMemo(() => {
    return Math.ceil(loyaltyHistoryData.historyItemsLength / itemsPerPage);
  }, [loyaltyHistoryData.historyItemsLength]);

  const getPageNumbers = useCallback((totalPages: number, currentPage: number) => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage < 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage > totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  }, []);

  const renderHistoryItem = (historyItem: ILoyaltyHistory) => {
    let itemType: "Expired" | "Receive" | "Deduction";
    let itemTitle: string;
    let description: string = "";
    const date = dayjs(historyItem.createdAt).format("DD/MM/YYYY");
    if (historyItem.isExpired) {
      itemType = "Expired";
      itemTitle = t("YouEarnedPoints", { points: historyItem.numberOfPoints });
      description =
        t("YourPointsWasExpired", { points: historyItem.numberOfPoints }) +
        " (" +
        t("ThesePointsExpiredOn") +
        " " +
        dayjs(historyItem.expireDate).format("DD/MM/YYYY") +
        ")";
    } else if (historyItem.chargeType === LoyaltyHistoryChargeTypes.Receive) {
      itemType = "Receive";
      itemTitle = t("YouEarnedPoints", { points: historyItem.numberOfPoints });
      if (historyItem.isFromReferral) {
        description = t("YouGotPointsForInvitingAFriend", { points: historyItem.numberOfPoints });
      } else if (historyItem.dropinReservationId) {
        description = t("YouMadeADropInReservationForAndReceived", {
          points: historyItem.numberOfPoints,
          price: historyItem.itemFinalPrice,
        });
      } else if (historyItem.userSubscriptionId) {
        description = t("YouBoughtASubscriptionForAndReceived", {
          points: historyItem.numberOfPoints,
          price: historyItem.itemFinalPrice,
        });
      }
      if (historyItem.expireDate) {
        description +=
          " (" +
          t("ThesePointsWillExpireOn") +
          " " +
          dayjs(historyItem.expireDate).format("DD/MM/YYYY") +
          ")";
      }
    } else {
      itemType = "Deduction";
      itemTitle = t("YouRedeemedPoints", { points: historyItem.numberOfPoints });
      description = t("YouGotDiscountWithPointsYouUsed", {
        discountValue: historyItem.discountAmount,
      });
    }
    return (
      <div key={historyItem.id} className={`${styles.item} ${styles[itemType]}`}>
        <div>
          <h4>{itemTitle}</h4>
          <p className={styles.data}>{date}</p>
          <p className={styles.description}>{description}</p>
        </div>
        <h3 className={styles.points}>
          {itemType === "Deduction" ? "-" : "+"}
          {historyItem.numberOfPoints}
        </h3>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.left}>
          <h2>{t("LoyaltyPoints")}</h2>
          <p>{t("PointsBalance")}</p>
          <p className={styles.balance}>
            {loyaltyBalance ? loyaltyBalance.loyaltyPointsBalance : 0}
          </p>
          {loyaltyBalance &&
            loyaltyBalance.earliestExpirePoints &&
            loyaltyBalance.earliestExpireDate && (
              <span>
                {t("YouHavePointsAreExpiringOn", {
                  points: loyaltyBalance.earliestExpirePoints,
                  date: dayjs(loyaltyBalance.earliestExpireDate).format("DD/MM/YYYY"),
                })}
              </span>
            )}
          <div></div>
          <Link to={`how-it-works`}>{t("HowItWorks")}?</Link>
        </div>
        <div className={styles.right}>
          <img src={giftImage} alt={"Gift"} />
        </div>
      </div>
      <div className={styles.history}>
        <h2>{t("History")}</h2>
        <div>
          {loyaltyHistoryData.historyItemsLength ? (
            <>
              <div className={styles.wrapper}>
                {loyaltyHistoryData.historyItems.map((historyItem: ILoyaltyHistory) => {
                  return renderHistoryItem(historyItem);
                })}
              </div>
              <div className={styles.pagination}>
                {getPageNumbers(totalPages, currentPage).map((page, index) =>
                  page === "..." ? (
                    <span key={index} className={styles.dots}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(Number(page))}
                      className={page === currentPage ? styles.activePage : ""}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
            </>
          ) : (
            <div className={styles.empty}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="188"
                height="188"
                viewBox="0 0 188 188"
                fill="none"
              >
                <g clipPath="url(#clip0_19434_10895)">
                  <path
                    d="M26.1636 54.8331C47.7914 17.3663 95.7001 4.5431 133.167 26.1631C148.011 34.7323 159.636 47.9324 166.261 63.7407C172.885 79.5489 174.143 97.0932 169.843 113.685C168.143 120.202 160.27 122.098 155.68 117.844L155.022 117.163L137.381 96.8981C132.015 90.7333 137.444 81.4508 145.191 82.7198L146.053 82.8999L156.095 85.5241C154.165 71.4214 147.492 58.3957 137.174 48.5908C126.855 38.7859 113.506 32.786 99.3231 31.5785C85.1403 30.371 70.9689 34.0278 59.1413 41.9472C47.3136 49.8666 38.5344 61.5766 34.2485 75.1501C29.9626 88.7237 30.4253 103.352 35.5605 116.627C40.6957 129.903 50.1974 141.035 62.5019 148.191C74.8064 155.347 89.1806 158.101 103.259 155.999C117.337 153.898 130.28 147.066 139.958 136.629C140.652 135.857 141.493 135.23 142.431 134.786C143.369 134.342 144.386 134.088 145.423 134.041C146.46 133.993 147.497 134.152 148.472 134.508C149.447 134.864 150.341 135.41 151.103 136.115C151.865 136.82 152.48 137.67 152.91 138.614C153.341 139.559 153.58 140.579 153.613 141.617C153.646 142.654 153.473 143.688 153.103 144.658C152.733 145.628 152.173 146.515 151.458 147.266C127.159 173.516 87.1304 180.48 54.8336 161.836C17.3668 140.216 4.54359 92.2999 26.1636 54.8331ZM94.0003 46.9998C95.9189 47 97.7707 47.7044 99.2045 48.9794C100.638 50.2543 101.554 52.0111 101.779 53.9166L101.834 54.8331V90.7568L123.038 111.962C124.443 113.371 125.259 115.263 125.32 117.252C125.38 119.241 124.682 121.179 123.365 122.672C122.049 124.165 120.214 125.1 118.233 125.289C116.252 125.478 114.273 124.905 112.698 123.688L111.962 123.038L88.4621 99.5379C87.2446 98.3194 86.4627 96.7336 86.2374 95.0259L86.1669 93.9998V54.8331C86.1669 52.7556 86.9922 50.7631 88.4613 49.2941C89.9303 47.8251 91.9227 46.9998 94.0003 46.9998Z"
                    fill="#DCDCDC"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_19434_10895">
                    <rect width="188" height="188" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <h3>{t("YouHaveNoHistory")}</h3>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoyaltyPoints;
